import * as Icons from '@ant-design/icons';
import { useSelect } from '@refinedev/antd';
import { useInvalidate, useOne, useNavigation } from '@refinedev/core';
import { DatePicker, Flex, Form, Select, Typography } from 'antd';
import { PreviewCertificate } from 'components/certificates/PreviewCertificate';
import { CustomModal } from 'components/modal/CustomModal';
import dayjs from 'dayjs';
import useNotification from 'hooks/useNotification';
import { IIssueCertificate } from 'interfaces/course_certificate';
import {
  ISignaturePartners,
  ISignaturePartnersGet
} from 'interfaces/signature_partners';
import { useCallback, useEffect } from 'react';
import { useCertificateStore } from '../../certificate.store';

export const ModalCreateCertificate = () => {
  const {
    enrollment,
    isIssuingCertificate,
    issueCertificate,
    setSignaturePartner,
    modals: { createCertificate },
    closeCreateCertificateModal
  } = useCertificateStore();

  const [form] = Form.useForm<IIssueCertificate>();
  const { showError, showSuccess } = useNotification();
  const invalidate = useInvalidate();
  const { push } = useNavigation();

  const { selectProps } = useSelect<ISignaturePartners>({
    resource: 'certifiers/signature_partners',
    optionLabel: 'name',
    optionValue: 'id',
    filters: [
      {
        field: 'filter[by_report_kind]',
        operator: 'eq',
        value: 'student_graduate_course_certificate'
      }
    ]
  });

  const signaturePartnerId = Form.useWatch('signature_partner_id', form);

  // Usar useOne do Refine para buscar signature partner
  const { data: signaturePartnerData } = useOne<ISignaturePartnersGet>({
    resource: 'certifiers/signature_partners',
    id: signaturePartnerId as string,
    queryOptions: {
      enabled: !!signaturePartnerId
    }
  });

  const handleCreateCertificate = useCallback(async () => {
    if (!enrollment?.id) {
      showError('Dados da matrícula não encontrados.');
      return;
    }

    try {
      const formData = await form.validateFields();

      await issueCertificate(
        {
          ...formData,
          enrollment_id: enrollment.id,
          observations: ''
        },
        enrollment.certificate_id || undefined,
        {
          onProcessing: () => {
            // Fechar modal
            closeCreateCertificateModal();
            form.resetFields();

            // Mostrar notificação de processamento
            showSuccess(
              'Certificado está sendo processado. Você será notificado quando estiver pronto para download.'
            );

            // Redirecionar para a aba de informações do certificado
            push(`${window.location.pathname}?tab=certificate_info`);
          },
          onProcessed: () => {
            // Invalidar queries para atualizar os dados
            invalidate({
              resource: 'graduate/courses/enrollments',
              invalidates: ['detail']
            });

            // Mostrar notificação de sucesso
            showSuccess(
              'Certificado processado com sucesso! Agora você pode fazer o download.'
            );
          },
          onError: () => {
            // Invalidar queries para atualizar os dados
            invalidate({
              resource: 'graduate/courses/enrollments',
              invalidates: ['detail']
            });

            // Mostrar notificação de erro
            showError(
              'Erro no processamento do certificado. Entre em contato com o suporte.'
            );
          }
        }
      );
    } catch (error) {
      console.error('Error creating certificate:', error);
      showError(
        'Erro ao emitir certificado, tente novamente em alguns instantes.'
      );
    }
  }, [
    form,
    enrollment,
    issueCertificate,
    showError,
    showSuccess,
    closeCreateCertificateModal,
    invalidate,
    push
  ]);

  // Atualizar store quando signature partner for carregado
  useEffect(() => {
    if (signaturePartnerData?.data) {
      setSignaturePartner(signaturePartnerData.data);
    } else {
      setSignaturePartner(null);
    }
  }, [signaturePartnerData, setSignaturePartner]);

  // Reset form when modal closes
  useEffect(() => {
    if (!createCertificate) {
      form.resetFields();
      closeCreateCertificateModal();
      setSignaturePartner(null);
    }
  }, [
    createCertificate,
    form,
    setSignaturePartner,
    closeCreateCertificateModal
  ]);

  return (
    <CustomModal
      open={createCertificate}
      title="Emitir certificado?"
      okText="Emitir certificado"
      centered
      cancelText="Cancelar"
      onOk={handleCreateCertificate}
      okButtonProps={{
        loading: isIssuingCertificate
      }}
      destroyOnClose
      onCancel={() => closeCreateCertificateModal()}
      icon={<Icons.AuditOutlined />}
      width={700}
    >
      <Form layout="vertical" form={form}>
        <Flex gap={16}>
          <Form.Item
            label="Data de registro"
            name="registered_at"
            initialValue={dayjs()}
            rules={[{ required: true, message: 'Campo obrigatório.' }]}
            style={{ flex: 1 }}
          >
            <DatePicker
              className="w-full"
              placeholder="dd/mm/aaaa"
              format="DD/MM/YYYY"
              disabled
            />
          </Form.Item>

          <Form.Item
            label="Data de emissão"
            name="issued_at"
            initialValue={dayjs()}
            rules={[{ required: true, message: 'Campo obrigatório.' }]}
            style={{ flex: 1 }}
          >
            <DatePicker
              className="w-full"
              placeholder="dd/mm/aaaa"
              format="DD/MM/YYYY"
              disabled
            />
          </Form.Item>
        </Flex>

        <Form.Item
          label="Modelo"
          name="signature_partner_id"
          rules={[{ required: true, message: 'Campo obrigatório.' }]}
        >
          <Select
            {...selectProps}
            placeholder="Selecione um modelo de certificado"
          />
        </Form.Item>
      </Form>

      {/* Certificate Preview */}
      {signaturePartnerData?.data && (
        <Flex
          className="border solid border-1 border-gray rounded-md p-2 w-full bg-neutral-100 max-md:flex-col"
          align="stretch"
          justify="stretch"
          gap={16}
        >
          {signaturePartnerData.data.front_signature &&
            signaturePartnerData.data.back_signature && (
              <Flex vertical gap={4}>
                <Typography.Text strong className="!mb-0">
                  Frente
                </Typography.Text>
                <PreviewCertificate
                  type="front"
                  signaturePartner={signaturePartnerData.data}
                />
              </Flex>
            )}

          {signaturePartnerData.data.record_back_signature && (
            <Flex vertical gap={4} className="min-h-full">
              <Typography.Text strong className="!mb-0">
                Verso
              </Typography.Text>
              <PreviewCertificate
                type="back"
                signaturePartner={signaturePartnerData.data}
              />
            </Flex>
          )}
        </Flex>
      )}
    </CustomModal>
  );
};
